/**
 * Resolución de salida de una imagen generada — 1K, 2K o 4K.
 *
 * **Espejo del backend**: `atlas-backend/src/billing/costs.ts` (`IMAGE_SIZES`,
 * `imageCreditsFor`). El valor de `id` es literalmente el que viaja en el body como
 * `image_size` y el que el backend le pasa a Gemini en `imageConfig.imageSize`.
 * Si cambia la tarifa allá, cambia acá.
 *
 * Tarifa desde 2026-10-16: 1K y 2K = 1 crédito, 4K = 2. Con Nano Banana 2.1 una imagen
 * 2K cuesta lo mismo que la 1K de gemini-3.1 (~$0,085) contra el ancla de ~$0,08 por
 * crédito de `utils/credits.ts`, así que 2K pasó a ser el default
 * (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto; la tarifa anterior, 1/2/3, en
 * ADR-2026-08-25-resolucion-de-generacion-2k-4k).
 *
 * ⚠️ Lo que se muestra acá es lo que se RESERVA. El cobro real puede terminar siendo
 * menor: el backend cobra por la resolución que efectivamente entregó, así que si la
 * cadena cae a otro proveedor que no llega a 4K, el usuario paga por lo que recibió.
 * Nunca al revés — lo pedido es el techo.
 */
export const RESOLUCIONES = ['1K', '2K', '4K'] as const;

export type ResolucionImagen = (typeof RESOLUCIONES)[number];

/** La que sale si nadie toca el selector: 2K, al mismo precio que 1K. */
export const RESOLUCION_DEFAULT: ResolucionImagen = '2K';

const CREDITOS: Record<ResolucionImagen, number> = { '1K': 1, '2K': 1, '4K': 2 };

/** Créditos que cuesta UNA imagen en esta resolución. */
export function creditosPorResolucion(res: ResolucionImagen | null | undefined): number {
  return CREDITOS[normalizarResolucion(res)];
}

/**
 * "1 crédito por imagen" / "2 créditos por imagen", sacado de la TARIFA. Lo usan las
 * notas de cada opción del selector: antes tres pantallas lo armaban con la posición de la
 * opción en la lista (`i + 1`), que dejó de coincidir con la tarifa el día que 2K pasó a
 * costar 1 (ADR-2026-10-16-nano-banana-2-1-en-2k-por-defecto).
 */
export function creditosPorImagenTexto(res: ResolucionImagen | null | undefined): string {
  const n = creditosPorResolucion(res);
  return `${String(n)} ${n === 1 ? 'crédito' : 'créditos'} por imagen`;
}

/** Sanea cualquier valor a una resolución válida. Ante la duda, la barata. */
export function normalizarResolucion(
  valor: string | null | undefined,
): ResolucionImagen {
  const v = String(valor ?? '').trim().toUpperCase();
  return (RESOLUCIONES as readonly string[]).includes(v)
    ? (v as ResolucionImagen)
    : RESOLUCION_DEFAULT;
}

export interface OpcionResolucion {
  id: ResolucionImagen;
  /** Lo que se ve en la fila y en el menú. */
  label: string;
  /** Para qué sirve, en una línea. */
  detalle: string;
}

export const OPCIONES_RESOLUCION: OpcionResolucion[] = [
  { id: '1K', label: 'Liviana (1K)', detalle: 'Archivo más chico. Cuesta lo mismo que la Estándar.' },
  { id: '2K', label: 'Estándar (2K)', detalle: 'La de siempre: nítida para redes y para hacer zoom.' },
  { id: '4K', label: 'Máxima (4K)', detalle: 'Todo el detalle. Para imprimir o gráfica grande.' },
];

/** Etiqueta de una resolución (la que muestra la fila cerrada). */
export function labelResolucion(res: ResolucionImagen | null | undefined): string {
  const id = normalizarResolucion(res);
  return OPCIONES_RESOLUCION.find((o) => o.id === id)?.label ?? id;
}
